// =============================================================================
// FILE: frontend/src/modules/ppe/pages/mes/services/mesExecutionService.js
//
// MODULE: PPE → MES (Shopfloor Execution)
//
// PURPOSE:
//   Central API layer for MES shopfloor screens.
//
// ARCHITECTURE:
//   Component → MES Service → Backend API
//
// IMPORTANT:
//   ✔ Injects tenant/location scope (MANDATORY for PPE APIs)
//   ✔ Uses MES CONTROL APIs for lifecycle
//   ✔ Uses MES LOGGING APIs for runtime data
//   ✔ Keeps UI completely backend-agnostic
// =============================================================================

import axiosClient from "../../../../../../api/axiosClient";

// -----------------------------------------------------------------------------
// Scope Helper (TaskFlow Standard)
// -----------------------------------------------------------------------------
const getScopeParams = () => {
  try {
    const scope = JSON.parse(localStorage.getItem("scope")) || {};
    return {
      tenant_id: scope.tenant_id,
      location_id: scope.location_id,
    };
  } catch {
    return {};
  }
};

// -----------------------------------------------------------------------------
// MES CONTROL APIs (Lifecycle Authority)
// -----------------------------------------------------------------------------

/**
 * Get READY operations for shopfloor dashboard
 */
export const fetchReadyOperations = async () => {
  const res = await axiosClient.get(
    "/api/ppe/mes-control/shopfloor/ready",
    {
      params: getScopeParams(),
    }
  );

  return res.data.data;
};


/**
 * Start execution (MES controlled)
 */
export const startExecution = async (planActivityId) => {
  const res = await axiosClient.post(
    "/api/ppe/mes-control/start",
    {
      plan_activity_id: planActivityId,
    },
    {
      params: getScopeParams(),
    }
  );

  return res.data.data;
};


/**
 * Get currently running executions
 */
export const fetchRunningExecutions = async () => {
  const res = await axiosClient.get(
    "/api/ppe/mes-control/running",
    {
      params: getScopeParams(),
    }
  );

  return res.data.data;
};


/**
 * Complete execution
 */
export const completeExecution = async (executionId) => {
  const res = await axiosClient.post(
    `/api/ppe/mes-control/complete/${executionId}`,
    {},
    {
      params: getScopeParams(),
    }
  );

  return res.data.data;
};


// -----------------------------------------------------------------------------
// MES LOGGING APIs (Existing Phase6 APIs)
// -----------------------------------------------------------------------------

/**
 * Log production output
 */
/**
 * Log production output
 */
export const logOutput = async (
  executionId,
  goodQty,
  rejectedQty
) => {
  try {
    const res = await axiosClient.post(
      `/api/ppe/mes/executions/${executionId}/output`,
      {
        good_qty: goodQty,
        rejected_qty: rejectedQty,
      },
      {
        params: getScopeParams(),
      }
    );

    // ✅ IMPORTANT — backend validation handling
    if (res.data?.success === false) {
      throw new Error(res.data.message || "Output logging failed");
    }

    return res.data;

  } catch (err) {
    // ✅ Normalize backend error for UI toast
    const message =
      err?.response?.data?.message ||
      err?.message ||
      "Failed to log output";

    throw new Error(message);
  }
};

/**
 * Put execution on HOLD
 */
export const holdExecution = async (
  executionId,
  reasonCode
) => {
  const res = await axiosClient.post(
    `/api/ppe/mes/executions/${executionId}/hold`,
    {
      reason_code: reasonCode,
    },
    {
      params: getScopeParams(),
    }
  );

  return res.data.data;
};


/**
 * Log worker participation
 */
export const logWorkerActivity = async (
  executionId,
  workerId,
  hoursSpent
) => {
  const res = await axiosClient.post(
    `/api/ppe/mes/executions/${executionId}/workers`,
    {
      worker_id: workerId,
      hours_spent: hoursSpent,
    },
    {
      params: getScopeParams(),
    }
  );

  return res.data.data;
};


/**
 * Manual time logging
 */
export const logExecutionTime = async (
  executionId,
  startTime,
  endTime
) => {
  const res = await axiosClient.post(
    `/api/ppe/mes/executions/${executionId}/time-log`,
    {
      start_time: startTime,
      end_time: endTime,
    },
    {
      params: getScopeParams(),
    }
  );

  return res.data.data;
};